import React, { useState } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import { sendRecoveryCode, confirmForgotPassword } from "../services/cognito";

interface RecoverViewProps {
  isDark: boolean;
  onCancel: () => void;
}

type RecoveryStep = "SEND_CODE" | "CONFIRM_RESET";

export const RecoverView: React.FC<RecoverViewProps> = ({ isDark, onCancel }) => {
  const [recoveryStep, setRecoveryStep] = useState<RecoveryStep>("SEND_CODE");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryNewPassword, setRecoveryNewPassword] = useState("");
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const [recoverySuccessMessage, setRecoverySuccessMessage] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const [showRecPass, setShowRecPass] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError("");
    setRecoverySuccessMessage("");

    if (!recoveryEmail.trim()) {
      setRecoveryError("Por favor, ingresá tu correo electrónico.");
      return;
    }
    setRecoveryLoading(true);
    try {
      await sendRecoveryCode(recoveryEmail.trim());
      setRecoveryStep("CONFIRM_RESET");
    } catch (err: any) {
      setRecoveryError(err.message || "Error al enviar el código de recuperación.");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError("");
    setRecoverySuccessMessage("");

    if (!recoveryCode.trim() || !recoveryNewPassword.trim()) {
      setRecoveryError("Por favor completá todos los campos.");
      return;
    }
    if (recoveryNewPassword !== recoveryConfirmPassword) {
      setRecoveryError("Las contraseñas no coinciden.");
      return;
    }
    if (recoveryNewPassword.length < 8) {
      setRecoveryError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setRecoveryLoading(true);
    try {
      await confirmForgotPassword(recoveryEmail.trim(), recoveryCode.trim(), recoveryNewPassword);
      setRecoverySuccessMessage("Contraseña restablecida con éxito. Ya podés iniciar sesión.");
      setTimeout(() => {
        onCancel();
      }, 3000);
    } catch (err: any) {
      setRecoveryError(err.message || "Error al cambiar la contraseña.");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleResend = async () => {
    setRecoveryError("");
    setRecoverySuccessMessage("");
    try {
      await sendRecoveryCode(recoveryEmail.trim());
      setRecoverySuccessMessage("Código de verificación reenviado con éxito.");
    } catch (err: any) {
      setRecoveryError(err.message || "Error al reenviar el código.");
    }
  };

  return (
    <div>
      <p className={`text-xs text-center leading-relaxed mb-3 ${isDark ? "text-white/40" : "text-gray-500"}`}>
        {recoveryStep === "SEND_CODE"
          ? "Ingresá tu correo electrónico para recibir un código de recuperación."
          : `Ingresá el código enviado a ${recoveryEmail} y tu nueva contraseña.`}
      </p>

      {recoveryError && (
        <div className="flex items-center gap-2 px-4 py-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <span>⚠️</span>
          <span>{recoveryError}</span>
        </div>
      )}

      {recoverySuccessMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <span>✅</span>
          <span>{recoverySuccessMessage}</span>
        </div>
      )}

      {/* Form */}
      {recoveryStep === "SEND_CODE" ? (
        <form onSubmit={handleSendCode} className="flex flex-col gap-4">
          <input
            type="email"
            placeholder="Correo electrónico"
            value={recoveryEmail}
            onChange={(e) => setRecoveryEmail(e.target.value)}
            disabled={recoveryLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
            autoFocus
          />

          <button
            type="submit"
            disabled={recoveryLoading}
            className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {recoveryLoading ? (
              <>
                <Spinner />
                <span>Enviando código...</span>
              </>
            ) : (
              "Enviar código de recuperación"
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className={`text-xs font-semibold text-center mt-2 ${isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"}`}
          >
            Volver al inicio de sesión
          </button>
        </form>
      ) : (
        <form onSubmit={handleConfirmReset} className="flex flex-col gap-4">
          {/* Code */}
          <input
            type="text"
            placeholder="Código de verificación (6 dígitos)"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            disabled={recoveryLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
            autoFocus
          />

          {/* Password */}
          <div className="relative">
            <input
              type={showRecPass ? "text" : "password"}
              placeholder="Nueva contraseña"
              value={recoveryNewPassword}
              onChange={(e) => setRecoveryNewPassword(e.target.value)}
              disabled={recoveryLoading}
              className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
                isDark
                  ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                  : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowRecPass(!showRecPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
            >
              {showRecPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {/* Confirm Password */}
          <input
            type="password"
            placeholder="Confirmar contraseña"
            value={recoveryConfirmPassword}
            onChange={(e) => setRecoveryConfirmPassword(e.target.value)}
            disabled={recoveryLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
          />

          <button
            type="submit"
            disabled={recoveryLoading}
            className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {recoveryLoading ? (
              <>
                <Spinner />
                <span>Restableciendo...</span>
              </>
            ) : (
              "Guardar nueva contraseña"
            )}
          </button>
{/* 
          <button
            type="button"
            onClick={handleResend}
            className={`text-xs font-semibold text-center mt-2 ${isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"}`}
          >
            Reenviar código
          </button> */}
        </form>
      )}
    </div>
  );
};
